import { Content } from "@/ui";
import { career_advice_helper } from "@/helpers/career_advice_helper";
import { getRoute } from "@/getters/getRoute";
import * as additionalComponents from "./__components";
import { createTitle } from "@/functions/createTitle";
import { getNestedRoutes } from "@/functions/getNestedRoutes";
import { sector_helper } from "@/helpers/sector_helper";
import { employer_helper } from "@/helpers/employer_helper";
import { jobs_helper } from "@/helpers/jobs_helper";
import { convertColorName } from "@/functions/convertColorName";
import { Helmet } from "react-helmet";
import sn from "@/snippets";
import useDynamicPage from "@/hooks/useDynamicPage";
import { getDynamicPageStaticProps } from "@/functions/getDynamicPageStaticProps";
import { DynamicPageSeo } from "@/ui/DynamicPageSeo";
import { ContentBlocks } from "@/ui/ContentBlocks";

export default function CareerAdviceSubPage({ data, prefix, content, title, canonical, seo, isBuilder }) {
  return (
    <>
      <Helmet>
        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css"
          integrity="sha512-z3gLpd7yknf1YoNbCzqRKc4qyor8gaKU1qmn+CShxbuBusANI9QpRohGBreCFkKxLhei6S9CQXFEbbKuqLg0DA=="
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
        />
      </Helmet>
      {isBuilder ? (
        <>
          <DynamicPageSeo
            {...seo}
            title={seo?.title.length > 0 ? seo.title : title}
            canonical={seo?.canonical.length > 0 ? seo.canonical : canonical}
          />
          <ContentBlocks data={data} prefix={prefix} />
        </>
      ) : (
        <Content items={content} logoCarousel={false} additionalComponents={additionalComponents} />
      )}
    </>
  );
}

export async function getStaticProps({ params: { url_slugs } }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const builderPage = useDynamicPage(`career-advice/${url_slugs}`);
  if (builderPage) {
    return getDynamicPageStaticProps({ page: builderPage, url_slugs });
  }

  const pages = getNestedRoutes({ url_slugs, routePrefix: `/career-advice/` });
  const [_page, _prevPage] = [...pages].reverse();
  // const page = career_advice_helper.find(_page.url_slug);
  // const parent = career_advice_helper.find(page.parent.id, "id") ?? null;
  // const children = career_advice_helper.fetch({ filter: (i) => i.parent.id === page.id });
  // const siblings = career_advice_helper.fetch({ filter: (i) => i.parent.id === page.parent.id });
  const pathParts = Array.isArray(url_slugs) ? url_slugs : [url_slugs];
  const isCategorized = pathParts.length > 1;

  // Get article slug (always the last part)
  const articleSlug = pathParts[pathParts.length - 1];
  const page = career_advice_helper.find(articleSlug);

  if (!page) {
    return { notFound: true };
  }

  // Validate category if present in URL
  if (isCategorized) {
    const urlCategory = pathParts[0];

    // Safely check if category exists
    let hasMatchingCategory = false;
    if (page.related_category) {
      if (Array.isArray(page.related_category)) {
        hasMatchingCategory = page.related_category.some(
          (cat) => String(cat).toLowerCase().replace(/\s+/g, "-") === urlCategory,
        );
      } else if (typeof page.related_category === "string") {
        hasMatchingCategory = String(page.related_category).toLowerCase().replace(/\s+/g, "-") === urlCategory;
      }
    }

    if (!hasMatchingCategory) {
      return { notFound: true };
    }
  }

  const textColor = convertColorName(page["header_text_color"]?.toLowerCase() ?? "white");
  const backgroundColor = convertColorName(page["header_background_color"]?.toLowerCase() ?? "light");

  // const sector = page["related_sector"] ? sector_helper.find(page["related_sector"].trim(), "title") : null;
  const sector = page["related_sector"] ? sector_helper.find(String(page["related_sector"]).trim(), "title") : null;
  const employer = page["related_employer"] ? employer_helper.find(page["related_employer"].trim(), "name") : null;
  const jobs =
    sector || employer
      ? jobs_helper.fetch({
          limit: 20,
          sector: sector ? sector.id : null,
          employer: employer ? employer.id : null,
          filter: (i) =>
            "related_jobs_keyword" in page && page["related_jobs_keyword"]
              ? i.title.toLowerCase().includes(page["related_jobs_keyword"].toLowerCase().trim())
              : true,
        })
      : null;

  const relatedArticlesItems = career_advice_helper
    .fetch({ limit: 3, featured: false })
    .filter((item) => item.related_category.includes(page["related_category"] ?? null))
    .filter((item) => item.related_sector.includes(page["related_sector"] ?? null));

  let content = [
    {
      component: "BreadcrumbNavigation",
      props: {
        items: [
          { label: "Jobs", href: getRoute("jobs") },
          { label: "Career Advice", href: getRoute("careerAdvice") },
          ...pages,
        ],
      },
    },
    {
      component: "Header",
      props: {
        className: `text-${textColor} bg-${backgroundColor}`,
        title: page.title,
        img: page.cover_image ?? null,
        variant: page["header_image_position"] ?? "Under Text",
        back: {
          placeholder: `Back to Career Advice`,
          href: getRoute("careerAdvice"),
        },
      },
    },
    {
      component: "CareerAdviceArticleContent",
      props: {
        id: page.id,
        title: page.title,
        body: page.content,
        embed_media: page.embed_media ?? null,
        url_slug: page.url_slug,
        url_slugs,
        // children,
        // siblings,
        // parent,
      },
    },
  ];

  page["additional_content"]?.map((item) =>
    content.push(
      sn.richText({
        page,
        config: {
          bodyKey: "additional_content",
        },
        contentId: item.title?.trim().replace(" ", "-").toLowerCase(),
        title: item.title ?? null,
        body: item.content,
      }),
    ),
  );

  content.push(
    sn.collapsibleSection({ page }),
    ...sn.narrativePanels({ page }),
    sn.promotionalCarousel({ page }),
    page["testimonials_items"] && page["testimonials_items"].length > 0
      ? sn.blockQuote({
          page,
          items: page["testimonials_items"]?.map((quote) => ({
            quote: quote.quote ?? null,
            author: {
              name: quote.author ?? null,
              position: quote.position ?? null,
              company: quote.company ?? null,
            },
          })),
        })
      : [],
    ...(jobs && jobs.length > 0
      ? [{ component: "LatestJobs", props: { title: page.related_jobs_title ?? null, items: jobs } }]
      : []),
    ...(relatedArticlesItems && relatedArticlesItems.length > 0
      ? [
          {
            component: "ArticleFeed",
            props: {
              title: "Related Articles",
              items: relatedArticlesItems,
              showSearchField: false,
              showCategoryFilter: false,
              showTagFilter: false,
              articleType: "careerAdviceArticle",
            },
          },
        ]
      : []),
    sn.multiCards({ page }),
  );

  return {
    props: {
      meta: {
        title: createTitle(page.title, "Career Advice"),
        canonical:
          "https://www.bluearrow.co.uk" +
          getRoute("careerAdviceArticle", { url_slug: pages.map((page) => page.url_slug).join("/") }),
      },
      content,
    },
  };
}

export async function getStaticPaths() {
  const articles = career_advice_helper.fetch();

  const paths = articles.flatMap((article) => {
    const basePaths = [];

    const categories = [];
    if (article.related_category) {
      if (Array.isArray(article.related_category)) {
        categories.push(...article.related_category);
      } else if (typeof article.related_category === "string") {
        categories.push(article.related_category);
      }
    }

    // disabled by ticket 149422270706 saying it should only be CV Templates with the extra url
    //
    // categories.forEach((category) => {
    //   const categorySlug = String(category).toLowerCase().replace(/\s+/g, "-");
    //   basePaths.push({
    //     params: { url_slugs: [categorySlug, article.url_slug] },
    //   });
    // });

    if (categories.includes("CV Templates")){
      basePaths.push({
          params: { url_slugs: ['cv-templates', article.url_slug] },
        });
    } else {
      basePaths.push({
        params: { url_slugs: [article.url_slug] },
      });
    }

    return basePaths;
  });

  return { paths, fallback: false };
}
